import React, { useState, useEffect, useMemo } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { AgentNotification } from '../../types';

interface NotificationGroup {
  type: AgentNotification['type'];
  title: string;
  icon: string;
  notifications: AgentNotification[];
  isExpanded: boolean;
}

const FloatingNotificationBar: React.FC = () => {
  const { 
    notifications, 
    visibleNotifications, 
    removeNotification, 
    executeNotificationAction, 
    snoozeNotification,
    showAllNotifications,
    hasHiddenNotifications 
  } = useNotifications();
  const [animationVisibleNotifications, setAnimationVisibleNotifications] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['customer_message', 'ticket_assignment', 'escalation', 'service_report_approval']));
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [isHidden, setIsHidden] = useState<boolean>(true); // Start hidden, only show when bell is clicked

  // Group notifications by type
  const notificationGroups: NotificationGroup[] = useMemo(() => {
    const groups: Record<string, NotificationGroup> = {};
    
    const typeConfig = {
      'ticket_assignment': { title: 'Ticket Assignments', icon: '🎫' },
      'customer_message': { title: 'Customer Messages', icon: '💬' },
      'agent_status': { title: 'Agent Updates', icon: '👥' },
      'unassigned_ticket': { title: 'Unassigned Tickets', icon: '⚠️' },
      'escalation': { title: 'Escalations', icon: '🚨' },
      'system': { title: 'System Notifications', icon: 'ℹ️' },
      'service_report_approval': { title: 'Report Approvals', icon: '📋' },
      'welcome': { title: 'Welcome Messages', icon: '👋' }
    };

    visibleNotifications.forEach(notification => {
      const type = notification.type;
      if (!groups[type]) {
        const config = typeConfig[type] || { title: 'Other', icon: '📋' };
        groups[type] = {
          type,
          title: config.title,
          icon: config.icon,
          notifications: [],
          isExpanded: expandedGroups.has(type)
        };
      }
      groups[type].notifications.push(notification);
    });

    // Sort notifications within each group by priority and timestamp
    Object.values(groups).forEach(group => {
      group.notifications.sort((a, b) => {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
    });

    return Object.values(groups);
  }, [visibleNotifications, expandedGroups]);

  // Handle notification visibility animations
  useEffect(() => {
    visibleNotifications.forEach(notification => {
      if (!animationVisibleNotifications.has(notification.id)) {
        setTimeout(() => {
          setAnimationVisibleNotifications(prev => new Set(prev).add(notification.id));
        }, 10);
      }
    });

    const currentIds = new Set(visibleNotifications.map(n => n.id));
    setAnimationVisibleNotifications(prev => {
      const newVisible = new Set<string>();
      prev.forEach(id => {
        if (currentIds.has(id)) {
          newVisible.add(id);
        }
      });
      return newVisible;
    });
  }, [visibleNotifications, animationVisibleNotifications]);

  const handleClose = (notification: AgentNotification) => {
    setAnimationVisibleNotifications(prev => {
      const newVisible = new Set<string>(prev);
      newVisible.delete(notification.id);
      return newVisible;
    });

    setTimeout(() => {
      removeNotification(notification.id);
    }, 300);
  };

  const handleSnooze = (notification: AgentNotification, minutes: number) => {
    setAnimationVisibleNotifications(prev => {
      const newVisible = new Set<string>(prev);
      newVisible.delete(notification.id);
      return newVisible;
    });

    setTimeout(() => {
      snoozeNotification(notification.id, minutes);
    }, 300);
  };

  const handleActionClick = (notification: AgentNotification) => {
    executeNotificationAction(notification);
  };

  const handleHidePanel = () => {
    setIsHidden(true);
  };

  const handleShowPanel = () => {
    setIsHidden(false);
    setIsMinimized(false); // Also un-minimize the panel
    if (hasHiddenNotifications) {
      showAllNotifications();
    }
  };

  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(type)) {
        newExpanded.delete(type);
      } else {
        newExpanded.add(type);
      }
      return newExpanded;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(notificationGroups.map(g => g.type)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const getBorderColor = (priority: AgentNotification['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500';
      case 'high':
        return 'border-l-orange-500';
      case 'medium':
        return 'border-l-yellow-500';
      case 'low':
        return 'border-l-blue-500';
      default:
        return 'border-l-gray-500';
    }
  };

  const getPriorityBadge = (priority: AgentNotification['priority']) => {
    const classes = {
      urgent: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${classes[priority]}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return 'now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const totalVisibleNotifications = visibleNotifications.length;
  const totalAllNotifications = notifications.length;

  // Always show the notification balloon as a permanent notification center access point
  // Only hide it if explicitly set to never show (which we don't do anymore)

  // Show persistent notification bell when panel is hidden
  if (isHidden || isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleShowPanel}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl"
          title={totalAllNotifications === 0 ? 'Notification Center (No notifications)' : `${totalAllNotifications} notification${totalAllNotifications !== 1 ? 's' : ''} ${hasHiddenNotifications ? '(some hidden)' : ''}`}
        >
          <div className="relative">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-4.5-5.5L16 10V6a4 4 0 00-8 0v4l.5 1.5L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {totalAllNotifications > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-md">
                {totalAllNotifications > 9 ? '9+' : totalAllNotifications}
              </span>
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-24 right-6 z-50 w-96 max-h-[70vh] flex flex-col transition-all duration-300 ${isMinimized ? 'w-64' : ''}`}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-t-lg shadow-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
            <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
              {totalVisibleNotifications}
            </span>
            {hasHiddenNotifications && (
              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-gray-500 rounded-full" title="Hidden/snoozed notifications">
                +{totalAllNotifications - totalVisibleNotifications}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            {!isMinimized && (
              <>
                <button
                  onClick={expandAll}
                  className="p-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Expand all groups"
                >
                  ⬇️
                </button>
                <button
                  onClick={collapseAll}
                  className="p-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Collapse all groups"
                >
                  ⬆️
                </button>
                {hasHiddenNotifications && (
                  <button
                    onClick={showAllNotifications}
                    className="p-1 text-xs text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
                    title="Show all hidden notifications"
                  >
                    👁️
                  </button>
                )}
              </>
            )}
            <button
              onClick={handleHidePanel}
              className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Close notification panel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Notification Groups */}
      {!isMinimized && (
        <div className="bg-white dark:bg-gray-800 border-x border-b border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg max-h-[60vh] overflow-y-auto">
          {notificationGroups.length === 0 ? (
            /* Empty state when no notifications */
            <div className="p-6 text-center">
              <div className="text-gray-400 dark:text-gray-500 mb-2">
                <svg className="w-12 h-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-4.5-5.5L16 10V6a4 4 0 00-8 0v4l.5 1.5L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No notifications</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">You're all caught up! New notifications will appear here.</p>
            </div>
          ) : (
            notificationGroups.map((group) => (
            <div key={group.type} className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.type)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{group.icon}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {group.title}
                  </span>
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-gray-600 rounded-full">
                    {group.notifications.length}
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    expandedGroups.has(group.type) ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Group Content */}
              {expandedGroups.has(group.type) && (
                <div className="space-y-1 pb-2">
                  {group.notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`
                        mx-3 mb-2 p-3 rounded-lg border-l-4 ${getBorderColor(notification.priority)}
                        bg-gray-50 dark:bg-gray-700/50 
                        border border-gray-200 dark:border-gray-600
                        transition-all duration-200 hover:shadow-md
                        ${animationVisibleNotifications.has(notification.id) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
                      `}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {notification.title}
                            </h4>
                            {getPriorityBadge(notification.priority)}
                          </div>
                          
                          <p className="text-xs text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatTimestamp(notification.timestamp)}
                            </span>
                            
                            <div className="flex items-center space-x-1">
                              {notification.actionButton && (
                                <button
                                  onClick={() => handleActionClick(notification)}
                                  className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                                >
                                  {notification.actionButton.text}
                                </button>
                              )}
                              
                              {/* Snooze dropdown */}
                              <div className="relative group">
                                <button
                                  className="p-0.5 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors"
                                  title="Snooze"
                                >
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                                
                                {/* Snooze options dropdown */}
                                <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-10 min-w-max">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleSnooze(notification, 5)}
                                      className="block w-full text-left px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      5 minutes
                                    </button>
                                    <button
                                      onClick={() => handleSnooze(notification, 15)}
                                      className="block w-full text-left px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      15 minutes
                                    </button>
                                    <button
                                      onClick={() => handleSnooze(notification, 30)}
                                      className="block w-full text-left px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      30 minutes
                                    </button>
                                    <button
                                      onClick={() => handleSnooze(notification, 60)}
                                      className="block w-full text-left px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      1 hour
                                    </button>
                                    <button
                                      onClick={() => handleSnooze(notification, 240)}
                                      className="block w-full text-left px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      4 hours
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              <button
                                onClick={() => handleClose(notification)}
                                className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                title="Dismiss"
                              >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
          )}
        </div>
      )}
    </div>
  );
};

export default FloatingNotificationBar; 